import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API, inputCls, labelCls } from './groupMeta';

export const CONCERN_CATEGORIES = ['general', 'staff conduct', 'safeguarding', 'leadership', 'rules breach', 'inactivity', 'other'];
export const SEVERITY_HEX = { low: '#38BDF8', medium: '#F59E0B', high: '#F43F5E' };

export const RaiseConcernModal = ({ group, onClose, onSaved, getAuthHeaders }) => {
  const [f, setF] = useState({ category: 'general', severity: 'medium', description: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF({ category: 'general', severity: 'medium', description: '' }); }, [group]);

  const send = async () => {
    if (f.description.trim().length < 5) return toast.error('Describe the concern (at least 5 characters)');
    setSaving(true);
    try {
      await axios.post(`${API}/groups/${group.id}/concerns`, f, { headers: getAuthHeaders() });
      toast.success("Concern sent to the group's oversight and executives");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Could not send'); }
    setSaving(false);
  };

  return (
    <Dialog open={!!group} onOpenChange={onClose}>
      <DialogContent className="bg-[#0b0b16] border-white/[0.08] text-white max-w-lg" data-testid="raise-concern-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-200"><Flag className="w-4 h-4" /> Raise a concern · {group?.name}</DialogTitle></DialogHeader>
        <p className="text-[11px] text-white/45 -mt-1">Only the group's oversight and executives will see this. You'll be notified when it's resolved.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={`${inputCls} capitalize`} data-testid="concern-category">
              {CONCERN_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className={labelCls}>Severity</label>
            <div className="flex gap-1.5">{Object.entries(SEVERITY_HEX).map(([k, hex]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, severity: k })} data-testid={`concern-severity-${k}`}
                className="flex-1 py-2.5 rounded-xl text-[11px] capitalize border transition-colors"
                style={f.severity === k ? { background: `${hex}22`, borderColor: hex, color: hex } : { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>{k}</button>
            ))}</div></div>
        </div>
        <textarea rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What happened, when, and who was involved?"
          className={`${inputCls} resize-none`} data-testid="concern-description" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-white/50 hover:text-white">Cancel</button>
          <button onClick={send} disabled={saving} data-testid="concern-submit-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-amber-300 to-rose-300 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />} Send concern
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
